// The active radar axis list for a template — the module's custom axes override the template
// default, else the default (TEMPLATE_AXIS_ORDER). Resolved against the metric catalogue for
// full labels + output types. Built on the 04h-4a pure layer; consumed by the RadarModule
// (04h-4b), Similarity and the merged Radar.
//
// ?live=1: the goalkeeper template's Claim Success % (hidden — the engine's number was wrong, then
// removed) becomes engine v1.7.0's "Claimable Claim Success %" only, as Catalyst's Radar (Ben,
// 2026-10-26: "1a", with Catalyst's fixed scale, live/catalyst-radar-scale.ts). The Stats tile keeps
// both keeper stats (Q-015 B); CCAX % can still be added here as a custom spoke. Picked once, at
// load (`LIVE`): the snapshot site gets exactly the lists it always did.

import { useMemo } from "react";
import type { ProtoMetricCategory } from "@catbento/shared";
import { useMetricCategories } from "./useMetricCategories.js";
import { buildMetricCatalog, resolvedAxes, TEMPLATE_AXIS_ORDER, type RadarAxis } from "../modules/radar-view.js";
import { LIVE } from "../live/flag.js";
import { liveRadarKeys, liveStatsCategory } from "../live/stats.js";

function useLiveRadarCategories(): ProtoMetricCategory[] {
  const categories = useMetricCategories();
  return useMemo(() => categories.map(liveStatsCategory), [categories]);
}

/** The catalogue the radars read (labels, ⓘ text, the axis editor's add list). */
export const useRadarCategories: () => ProtoMetricCategory[] = LIVE ? useLiveRadarCategories : useMetricCategories;

/** The axis keys on screen: the custom list if set, else the template default — on live with
 *  Claim Success % swapped for Claimable Claim Success % (an older saved custom list too). Pure. */
export function radarAxisKeys(template: string, customKeys: readonly string[] | null | undefined): string[] {
  const keys = customKeys && customKeys.length ? customKeys.slice() : (TEMPLATE_AXIS_ORDER[template] ?? []).slice();
  return LIVE ? liveRadarKeys(keys) : keys;
}

export function useRadarAxes(template: string, customKeys: readonly string[] | null | undefined): RadarAxis[] {
  const categories = useRadarCategories();
  // Passing the resolved keys as the custom list gives the same axes as the template default would.
  return useMemo(
    () => resolvedAxes(template, radarAxisKeys(template, customKeys), buildMetricCatalog(categories)),
    [categories, template, customKeys],
  );
}
